import { get } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { eq, or } from 'ember-truth-helpers';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import HotelAndMarketNavigationErnestNavigationButton from 'lumen/components/hotel-and-market/navigation/ernest-navigation-button';
import HotelAndMarketNavigationNavigationMainDropdown from 'lumen/components/hotel-and-market/navigation/navigation-main-dropdown';
import HotelAndMarketNavigationNavigationMainTabs from 'lumen/components/hotel-and-market/navigation/navigation-main-tabs';
import HotelAndMarketNavigationTabsTabMainNavigation from 'lumen/components/hotel-and-market/navigation/tabs/tab-main-navigation';
import ENV from 'lumen/config/environment';
import contextualPageTitle from 'lumen/helpers/contextual-page-title';
import { RESIZE_EVENTS } from 'lumen/services/resize';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { nextOverflowWidth } from 'lumen/utils/navigation-overflow';

import styles from './navigation-main.module.css';

interface HotelAndMarketNavigationNavigationMainSignature {
  Args: {};
}

export default class HotelAndMarketNavigationNavigationMain extends Component<HotelAndMarketNavigationNavigationMainSignature> {
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare resize: Services['resize'];
  @service declare router: Services['router'];
  @service declare tab: Services['tab'];

  @tracked intersectsWithHotelDropdown = false;
  // The width the navigation overflowed at, or null while it still fits
  @tracked overflowWidth: number | null = null;

  hasAddedResize: boolean = false;
  tabsResizeObserver: ResizeObserver | null = null;
  // Used for determining when we can show the tabs again
  widthThreshold: number | null = null;

  get biMenuItems(): ReturnType<typeof this.tab.menuItems.filter> {
    return this.tab.menuItems.filter((group) => group.name === 'bi');
  }

  get hasMultipleMarketParityProducts(): boolean {
    if (!this.access.hasParityInsightAccess) {
      return false; // Keep old behaviour until Parity Strategy is released for all markets
    }

    const hasOtherTab =
      this.access.hasParityManagerAccess ||
      this.access.hasRates ||
      this.tab.hasRevenueInsight;

    return hasOtherTab;
  }

  get hasUnifiedHotelView(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW,
    );
  }

  get isNavigationFull(): boolean {
    return this.overflowWidth !== null;
  }

  get nonBiMenuItems(): ReturnType<typeof this.tab.menuItems.filter> {
    return this.tab.menuItems.filter((group) => group.name !== 'bi');
  }

  get shouldGroup(): boolean {
    if (this.hotelAndMarketService.market) {
      return (
        this.hasMultipleMarketParityProducts ||
        this.tab.hasMultipleMarketBIProducts ||
        this.tab.hasMarketDistributionDashboards
      );
    }

    let numberOfProducts = 0;

    if (this.tab.hasOverviewDashboard) {
      numberOfProducts += 1;
    }

    // Only count rates/parity as a product if user actually has rates or parity
    // If they only have Events/PA without rates/parity, those should be individual tabs
    if (this.tab.hasRateInsight || this.tab.hasParityInsight) {
      numberOfProducts += 1;
    }

    if (this.tab.hasRevenueInsight) {
      numberOfProducts += 1;
    }

    if (this.tab.hasMarketInsight) {
      numberOfProducts += 1;
    }

    if (this.tab.hasBenchmarkInsight) {
      numberOfProducts += 1;
    }

    return numberOfProducts > 1; // Always group if we have multiple products active
  }

  // Hotel-only, matching where the tab it replaces used to appear. Goes once
  // the navigation is full, so it cannot end up unclickable underneath the
  // hotel selector, and the divider cannot be left behind on its own.
  get showErnest(): boolean {
    return (
      this.tab.hasErnest &&
      Boolean(this.hotelAndMarketService.hotel) &&
      !this.isNavigationFull
    );
  }

  willDestroy(): void {
    super.willDestroy();

    // Older browsers don't support this yet, but it has a performance benefit
    // https://pawelgrzybek.com/the-resize-observer-explained/
    if (window.ResizeObserver && this.tabsResizeObserver) {
      this.tabsResizeObserver.disconnect();
    } else if (this.hasAddedResize) {
      // In case of older browsers, fall back to resize service
      this.resize.unregisterResizeEvent(RESIZE_EVENTS.NAVIGATION);
    }
  }

  checkGrouping(navigation: HTMLElement): void {
    if (this.shouldGroup) {
      return; // We group regardless of size, so there is nothing to measure
    }

    const targetAvailableWidth = navigation.offsetWidth;
    const targetContentWidth = (navigation.children[0] as HTMLElement)
      .offsetWidth;

    // If we set a widthThreshold, that means we are currently grouping tabs
    if (this.widthThreshold) {
      // If the width of the container has passed the threshold, we can switch back to showing all tabs
      if (targetAvailableWidth > this.widthThreshold) {
        this.intersectsWithHotelDropdown = false;
        this.widthThreshold = null;
      }
    } else if (targetAvailableWidth <= targetContentWidth) {
      // If we did not set a threshold, that means we need to check if the content is bigger than the container
      // if so, start grouping

      this.widthThreshold = targetContentWidth;
      this.intersectsWithHotelDropdown = true;
    }
  }

  // Grouping the tabs is the first answer to a crowded navigation; this is what
  // is left once even that no longer fits.
  checkOverflow(navigation: HTMLElement): void {
    const next = nextOverflowWidth(navigation, this.overflowWidth);

    if (next !== this.overflowWidth) {
      this.overflowWidth = next;
    }
  }

  checkResize(navigation: HTMLElement): void {
    this.checkGrouping(navigation);
    this.checkOverflow(navigation);
  }

  @action handleContentResize(entries: ResizeObserverEntry[]): void {
    this.checkResize(entries[0]!.target as HTMLElement);
  }

  @action handleWindowResize(): void {
    this.checkResize(document.getElementById('main-navigation')!);
  }

  @action initializeResizeObserver(): void {
    // See willDestroy for explanations on the resizeObserver
    if (window.ResizeObserver) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.tabsResizeObserver = new ResizeObserver(this.handleContentResize);

      const target = document.getElementById('main-navigation')!;

      this.tabsResizeObserver.observe(target);
    } else {
      this.hasAddedResize = true;
      this.resize.registerResizeEvent(
        RESIZE_EVENTS.NAVIGATION,
        this.handleWindowResize.bind(this),
      );
      this.handleWindowResize(); // Trigger once on page load
    }
  }

  @action redirectToBI(): void {
    const { biRedirectId } = this.hotelAndMarketService;

    if (!biRedirectId) {
      return;
    }

    this.router.transitionTo('hotel', biRedirectId);
  }

  @action redirectToPerformanceDashboard(): void {
    const hotelId = this.hotelAndMarketService.hotel?.id;

    if (!hotelId) {
      return;
    }

    openUrl(
      `${getHostDomain({ frontendGateway: ENV.frontendGateway })}/apigateway/v1/app/performance-dashboard/?subscription_id=${hotelId}`,
    );
  }

  @action redirectToRateInsight(): void {
    const { rateiRedirectId } = this.hotelAndMarketService;

    if (!rateiRedirectId) {
      return;
    }

    this.router.transitionTo('hotel', rateiRedirectId);
  }

  <template>
    {{if
      this.tab.activeTabName
      (pageTitle (contextualPageTitle this.tab.activeTabName) replace=true)
    }}
    <nav
      class="header__navigation flex items-center justify-center"
      data-test-id="main-navigation"
      id="main-navigation"
      {{didInsert this.initializeResizeObserver}}
    >
      <div id="main-navigation-tabs">
        {{#if (or this.shouldGroup this.intersectsWithHotelDropdown)}}
          {{#each this.nonBiMenuItems as |group|}}
            {{! If group name is empty, render children as individual tabs instead of grouping }}
            {{#if (eq group.name undefined)}}
              <ul class={{styles.tabs-list}}>
                {{#each group.children as |item|}}
                  <HotelAndMarketNavigationTabsTabMainNavigation
                    {{! @glint-expect-error: Incorrect type }}
                    @item={{item}}
                  />
                {{/each}}
              </ul>
            {{else if (eq group.children.length 1)}}
              <ul class={{styles.tabs-list}}>
                <HotelAndMarketNavigationTabsTabMainNavigation
                  {{! @glint-expect-error: Incorrect type }}
                  @item={{get group.children "0"}}
                />
              </ul>
            {{else}}
              <HotelAndMarketNavigationNavigationMainDropdown
                {{! @glint-expect-error: Incorrect type }}
                @group={{group}}
              />
            {{/if}}
          {{/each}}
        {{else}}
          <HotelAndMarketNavigationNavigationMainTabs
            @menuItems={{this.nonBiMenuItems}}
          />
        {{/if}}

        {{! BI always renders as a dropdown regardless of grouping }}
        {{#each this.biMenuItems as |group|}}
          <HotelAndMarketNavigationNavigationMainDropdown
            {{! @glint-expect-error: Incorrect type }}
            @group={{group}}
          />
        {{/each}}

        {{#if this.tab.showBenchmarkInsightLightOrUpsellTab}}
          <ul class={{styles.tabs-list}}>
            <HotelAndMarketNavigationTabsTabMainNavigation
              @item={{this.tab.benchmarkInsightLightOrUpsellTab}}
            />
          </ul>
        {{/if}}
      </div>
      {{#if this.showErnest}}
        <div class={{styles.divider}}></div>
        <HotelAndMarketNavigationErnestNavigationButton class="ml-500" />
      {{/if}}
      {{#if this.access.hasPerformanceDashboard}}
        <div class={{styles.divider}}></div>
        <PrismButton
          @buttonType="secondary"
          @iconAfter="link-out"
          @label={{t "menu.performance_board"}}
          @onClick={{this.redirectToPerformanceDashboard}}
          class="ml-500"
          data-test-id="performance-board-button"
        />
      {{/if}}
      {{#unless this.hasUnifiedHotelView}}
        {{#if this.hotelAndMarketService.biRedirectId}}
          <div class={{styles.divider}}></div>
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.bi"}}
            @onClick={{this.redirectToBI}}
            class="ml-500"
            data-test-id="rate-insight-bi-redirect-button"
          />
        {{/if}}
        {{#if this.hotelAndMarketService.rateiRedirectId}}
          <div class={{styles.divider}}></div>
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.rate_insight"}}
            @onClick={{this.redirectToRateInsight}}
            class="ml-500"
            data-test-id="bi-rate-insight-redirect-button"
          />
        {{/if}}
      {{/unless}}
    </nav>
  </template>
}
